import { RouterState } from "@/interfaces/Router";
import { auth } from "@/lib/firebase";
import { isEmailValid, translateError } from "@/lib/utils";
import { LOGIN_ROUTE } from "@/shared/constants";
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Link,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { sendPasswordResetEmail } from "firebase/auth";
import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import AuthWrapper from "./AuthWrapper";
import LoadingOverlay from "./LoadingOverlay";

interface Notification {
  type: AlertColor;
  message: string;
}

const PasswordRecovery = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { state } = useLocation() as { state: RouterState };
  const emailFromRouter = state?.email || "";

  // $ Получаем токен приглашения из URL параметров
  const [invitationToken, setInvitationToken] = useState<string | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("invitationToken");
    if (token) {
      setInvitationToken(token);
    }
  }, []);

  const [wasValidated, setWasValidated] = useState(false);
  const [email, setEmail] = useState(emailFromRouter);
  // notification is ref, not state, because it's value shouldn't be changing together with inputs. And setWasValidated will trigger re-render anyway when notification should be shown
  const notificationRef = useRef<Notification | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const onSuccess = () => {
    notificationRef.current = {
      type: "success",
      message: t("resetPassword.success"),
    };
    setWasValidated(false);
  };

  const onError = (error: unknown) => {
    notificationRef.current = {
      type: "error",
      message: translateError(error),
    };
    console.error(error);

    // reset errors, since server return an error, any field could be invalid
    setWasValidated(false);
  };

  const resetPasswordMutation = useMutation({
    mutationFn: async (email: string) => {
      await sendPasswordResetEmail(auth, email);
    },
    onSuccess,
    onError,
  });

  const navigateTo = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    route: string
  ) => {
    event.preventDefault();

    const emailValue = emailRef.current?.value || "";
    const state: RouterState = { email: emailValue };

    // $ Добавляем токен к URL если он существует
    if (invitationToken) {
      navigate(`${route}?invitationToken=${invitationToken}`, {
        state,
      });
    } else {
      navigate(route, {
        state,
      });
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = event.target.value;
    setEmail(emailValue);

    validateEmail(emailValue);
  };

  const validateEmail = (email: string): boolean => {
    if (!isEmailValid(email)) {
      emailRef.current?.setCustomValidity(t("errors.invalidEmail"));
      return true;
    } else {
      emailRef.current?.setCustomValidity("");
      return false;
    }
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async event => {
    event.preventDefault();
    notificationRef.current = null;
    setWasValidated(true);

    const form = event.currentTarget;

    validateEmail(email);
    if (!form.checkValidity()) {
      return;
    }

    resetPasswordMutation.mutate(email);
  };

  return (
    <AuthWrapper>
      <LoadingOverlay isLoading={resetPasswordMutation.isPending} />

      <Box sx={{ width: "100%", maxWidth: 400 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography variant="h3" sx={{ mb: 1, fontWeight: 700 }}>
            {t("resetPassword.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("resetPassword.subtitle")}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t("shared.rememberedPassword")}{" "}
              <Link
                component={RouterLink}
                to={`/${LOGIN_ROUTE}`}
                onClick={event => navigateTo(event, `/${LOGIN_ROUTE}`)}
                sx={{
                  textDecoration: "none",
                  fontWeight: 500,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {t("login.signIn")}
              </Link>
            </Typography>
          </Box>
        </Box>

        {/* Form */}
        <Box component="form" noValidate onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {notificationRef.current !== null && (
              <Alert
                severity={notificationRef.current.type}
                sx={{ borderRadius: 2 }}
              >
                {notificationRef.current.message}
              </Alert>
            )}

            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              inputRef={emailRef}
              fullWidth
              error={wasValidated && emailRef.current?.validity.valid === false}
              helperText={wasValidated && emailRef.current?.validationMessage}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ py: 1.5 }}
            >
              {t("resetPassword.submit")}
            </Button>
          </Stack>
        </Box>
      </Box>
    </AuthWrapper>
  );
};

export default PasswordRecovery;
